/**
 * What an HTTP status from the Telegram endpoints means for the sign-in screen.
 *
 * Pure on purpose: the caller passes the status it got (or `null` when no response
 * arrived), so the mapping is testable without a network, a router or a query client.
 */
import { AUTH_MESSAGES } from "./messages";

export type TelegramSignInOutcome =
  | "success"
  | "not-linked"
  | "unconfigured"
  | "refused"
  | "failed";

/**
 * - 2xx: signed in.
 * - 401: the payload is genuine but no staff account is connected to it, or it was
 *   refused. The server gives one answer for both, and so does the screen.
 * - 503: the deployment has no bot token. Our misconfiguration, not the person's.
 * - 400: the payload did not verify.
 * - anything else, including no response at all: a plain failure.
 */
export function telegramSignInOutcome(status: number | null): TelegramSignInOutcome {
  if (status === null) {
    return "failed";
  }
  if (status >= 200 && status < 300) {
    return "success";
  }
  if (status === 401) {
    return "not-linked";
  }
  if (status === 503) {
    return "unconfigured";
  }
  if (status === 400) {
    return "refused";
  }
  return "failed";
}

/** The sentence shown for a non-success outcome. `success` has none. */
export function telegramSignInMessage(outcome: Exclude<TelegramSignInOutcome, "success">): string {
  switch (outcome) {
    case "not-linked":
      return AUTH_MESSAGES.telegramNotLinked;
    case "unconfigured":
      return AUTH_MESSAGES.telegramUnconfigured;
    case "refused":
      return AUTH_MESSAGES.telegramRefused;
    case "failed":
      return AUTH_MESSAGES.genericError;
  }
}
